import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalDescription,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
} from '@/components/extension/responsive-modal'

import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp'
import {
  useForgotVerifyOtpMutation,
  useSendOtpMutation,
  useVerifyOtpAuthMutation,
  useVerifyOtpMutation,
} from '@/hooks/react-query/auth'

import { toast } from '@/components/common/toast'
import { useCountdown } from '@/hooks/common/use-countdown'
import { cn, getValue } from '@/lib/utils'
import { EApiStatus } from '@/types/api'
import { TVerifyOtpAuthParams } from '@/types/auth'
import { RotateCcw } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'
import { Button } from '../ui/button'

type Props = {
  onClose: () => void
  phone: string
  onSuccess?: (value: string) => void
  //TODO: Đinh nghĩa type
  type:
    | 'forgot_password'
    | 'login'
    | 'register'
    | 'update_phone'
    | 'confirm-info'
  userId?: string
  isAuth?: boolean
  bodyAuth?: Pick<
    TVerifyOtpAuthParams,
    'fullname' | 'email' | 'user_id' | 'phone'
  >
}

export const OtpForm = ({
  onClose,
  phone,
  onSuccess,
  type,
  bodyAuth,
  isAuth,
}: Props) => {
  const { seconds, setSeconds, start } = useCountdown({
    initialSeconds: 59,
    onComplete: () => console.info('Countdown finished!'),
    autoStart: true,
  })

  const [otp, setOtp] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [isVisible, setIsVisible] = useState(true)
  const t = useTranslations('login')

  const { mutateAsync: verifyOtp, isPending } = useVerifyOtpMutation()
  const { mutateAsync: sendOtp, isPending: isSending } = useSendOtpMutation()
  const { mutateAsync: verifyOtpAuth, isPending: isVerifying } =
    useVerifyOtpAuthMutation()

  const { mutateAsync: forgotVerifyOtp, isPending: isForgotVerifying } =
    useForgotVerifyOtpMutation()

  const handleClose = () => {
    setIsVisible(false)
    setTimeout(() => {
      onClose?.()
    }, 300)
  }

  const handleVerifyOtp = async () => {
    try {
      const res = await verifyOtp({ phone, otp, type })

      if (res?.status === EApiStatus.SUCCESS) {
        // Call onSuccess first, then close popup
        onSuccess?.(otp)
        handleClose()
      } else {
        setErrorMessage(getValue(res, 'message', t('otp_invalid')))
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      toast.message({
        title: t('error_occurred'),
        message: t('try_again'),
      })
    }
  }

  const handleVerifyOtpAuth = async () => {
    try {
      await verifyOtpAuth({
        phone,
        otp,
        type,
        user_id: bodyAuth?.user_id || '',
        email: bodyAuth?.email,
        fullname: bodyAuth?.fullname,
      })

      // Call onSuccess first, then close popup
      onSuccess?.(otp)
      handleClose()

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      if (error instanceof Error) {
        setErrorMessage(error.message)
      } else if (
        typeof error === 'object' &&
        error !== null &&
        'message' in error
      ) {
        setErrorMessage((error as { message: string }).message)
      }
    }
  }

  const handleForgotPasswordVerifyOtp = async () => {
    try {
      const res = await forgotVerifyOtp({
        otp,
        phone,
      })

      if (res?.state) {
        onSuccess?.(getValue(res, 'data.forgot_code', ''))
      } else {
        setErrorMessage(getValue(res, 'message', ''))
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: unknown) {
      if (error instanceof Error) {
        setErrorMessage(error.message)
      } else if (
        typeof error === 'object' &&
        error !== null &&
        'message' in error
      ) {
        setErrorMessage((error as { message: string }).message)
      } else {
        setErrorMessage(t('error_occurred'))
      }
    }
  }

  const handleSubmitOtp = async () => {
    if (type === 'forgot_password' && isAuth) {
      await handleForgotPasswordVerifyOtp()
      return
    }

    if (isAuth) {
      await handleVerifyOtpAuth()
      return
    }

    await handleVerifyOtp()
  }

  const handleResendOtp = async () => {
    try {
      const res = await sendOtp({
        address: phone,
        params: { type },
      })

      if (res?.delay) {
        setSeconds(res.delay)
        start()
      } else {
        setErrorMessage(getValue(res, 'message', t('otp_resend_fail')))
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      toast.message({
        title: t('error_occurred'),
        message: t('try_again'),
      })
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true)
    }, 50)

    return () => clearTimeout(timer)
  }, [])

  return (
    <ResponsiveModal
      open={isVisible}
      onOpenChange={(open) => !open && handleClose()}
    >
      <ResponsiveModalContent
        className='gap-0 border-none p-0 px-6 py-4 pb-3 lg:w-[430px] lg:rounded-md'
        onInteractOutside={(e) => e.preventDefault()}
        aria-describedby={'modal-login'}
      >
        <ResponsiveModalTitle className='border-b border-b-neutral-300 pb-2.5 text-center font-criteria text-lg font-normal leading-5 text-neutral-900'>
          {t('otp_title')}
        </ResponsiveModalTitle>
        <ResponsiveModalDescription />
        <div className='mt-2'>
          <p className='mb-4 text-center font-sans text-sm text-neutral-900'>
            {t('otp_instruction', { phone })}
          </p>

          <InputOTP
            maxLength={6}
            value={otp}
            className='justify-center'
            onChange={(value) => setOtp(value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && otp.length === 6) {
                handleSubmitOtp()
              }
            }}
          >
            <InputOTPGroup>
              <InputOTPSlot index={0} autoFocus />
              <InputOTPSlot index={1} />
              <InputOTPSlot index={2} />
              <InputOTPSlot index={3} />
              <InputOTPSlot index={4} />
              <InputOTPSlot index={5} />
            </InputOTPGroup>
          </InputOTP>
          <p className='mt-2 font-sans text-sm text-red-500'>{errorMessage}</p>

          <button
            className={cn(
              'mt-3.5 flex items-center gap-1 font-sans text-sm font-medium text-primary',
              seconds > 0 && 'text-neutral-400'
            )}
            onClick={handleResendOtp}
            disabled={seconds > 0 || isSending}
          >
            <RotateCcw size={14} strokeWidth={3} />{' '}
            <p>
              {t('otp_resend')}
              {seconds > 0 && t('otp_resend_after', { seconds })}
            </p>
          </button>
        </div>

        <ResponsiveModalFooter className='mt-5 border-t border-t-neutral-300 pt-3'>
          <Button
            size={'large'}
            className='w-full'
            variant='black'
            disabled={otp.length !== 6}
            onClick={handleSubmitOtp}
            loading={isPending || isVerifying || isForgotVerifying}
          >
            {t('confirm')}
          </Button>
        </ResponsiveModalFooter>
      </ResponsiveModalContent>
    </ResponsiveModal>
  )
}
